import { useState, useEffect } from 'react';
import axios from 'axios';
import { ThumbsUp, ThumbsDown, Check, Send, X, ArrowUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_PREFIX = 'dividrip:learn-feedback:';

/**
 * LearnGuideFeedback — Round P108ap (Feb 2026).
 *
 * Twin of `HowToSectionFeedback`, scoped to a single Learn guide
 * instead of a How-To section. Rendered by `LearnPage.jsx` right
 * above the Related-guides divider so every /learn/* page gets the
 * same "Was this guide helpful?" thumbs strip without editing each
 * of the 69 guide files.
 *
 * POSTs to `/api/learn/feedback` (anonymous allowed; signed-in users
 * get upsert-dedupe per (user_id, guide_path)). A 👎 vote opens a
 * 280-char comment textarea; 👍 skips it for zero-friction.
 */
export default function LearnGuideFeedback({ guidePath }) {
  const lsKey = LS_PREFIX + guidePath;
  const [current, setCurrent] = useState(() => {
    try { return localStorage.getItem(lsKey) || null; } catch { return null; }
  });
  const [submitting, setSubmitting] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [commentSaved, setCommentSaved] = useState(false);

  useEffect(() => {
    const onStorage = (e) => { if (e.key === lsKey) setCurrent(e.newValue || null); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [lsKey]);

  if (!guidePath) return null;

  const send = async (rating) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/learn/feedback`,
        { guide_path: guidePath, rating },
        { withCredentials: true },
      );
      setCurrent(rating);
      try { localStorage.setItem(lsKey, rating); } catch { /* swallow */ }
      if (rating === 'down') {
        setShowComment(true);
        setCommentSaved(false);
      } else {
        setShowComment(false);
        setComment('');
        setCommentSaved(false);
      }
    } catch {
      // Silent — Learn pages are public and feedback is best-effort.
    } finally {
      setSubmitting(false);
    }
  };

  const sendComment = async () => {
    const trimmed = (comment || '').trim().slice(0, 280);
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/learn/feedback`,
        { guide_path: guidePath, rating: 'down', comment: trimmed },
        { withCredentials: true },
      );
      setCommentSaved(true);
      setComment('');
    } catch {
      /* best-effort */
    } finally {
      setSubmitting(false);
    }
  };

  const Btn = ({ value, Icon, label, activeClass }) => {
    const isActive = current === value;
    return (
      <button
        type="button"
        onClick={() => send(value)}
        disabled={submitting}
        aria-pressed={isActive}
        title={`${label}${isActive ? ' (your current vote)' : ''}`}
        data-testid={`learn-feedback-${value}`}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors
          ${isActive
            ? activeClass
            : 'bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 dark:text-violet-300'}
          ${submitting ? 'opacity-60 cursor-wait' : ''}
        `}
      >
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
        {isActive && <Check className="w-3 h-3 opacity-80" />}
      </button>
    );
  };

  const charCount = (comment || '').length;
  const charsLeft = 280 - charCount;

  return (
    <div
      className="mt-10 pt-4 text-xs"
      data-testid="learn-guide-feedback"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-violet-800/85 dark:text-violet-300/70 mr-1 font-medium">Was this guide helpful?</span>
        <Btn
          value="up"
          Icon={ThumbsUp}
          label="Yes"
          activeClass="bg-emerald-500/20 text-emerald-800 dark:text-emerald-300"
        />
        <Btn
          value="down"
          Icon={ThumbsDown}
          label="Not yet"
          activeClass="bg-rose-500/20 text-rose-800 dark:text-rose-300"
        />
        {current === 'up' && (
          <span className="text-violet-700/75 dark:text-violet-300/55 ml-1">Thanks — vote recorded.</span>
        )}
        {current === 'down' && !showComment && !commentSaved && (
          <button
            type="button"
            onClick={() => setShowComment(true)}
            className="ml-1 underline decoration-dotted text-violet-700/85 dark:text-violet-300/70 hover:text-violet-900 dark:hover:text-violet-200"
            data-testid="learn-feedback-reopen-comment"
          >
            Tell me more →
          </button>
        )}
        {commentSaved && (
          <span className="text-emerald-700 dark:text-emerald-300 ml-1 font-medium">Got it — thanks for the detail.</span>
        )}
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
          className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:underline underline-offset-2 transition-colors"
          data-testid="learn-back-to-top"
        >
          <ArrowUp className="w-3 h-3" />
          Back to top
        </button>
      </div>

      {current === 'down' && showComment && !commentSaved && (
        <div
          className="mt-2 flex items-start gap-2"
          data-testid="learn-feedback-comment-form"
        >
          <div className="flex-1">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 280))}
              placeholder="What was unclear or missing in this guide?"
              rows={2}
              maxLength={280}
              disabled={submitting}
              className="w-full px-2.5 py-1.5 rounded-md text-xs bg-violet-50/60 dark:bg-violet-950/30 border border-violet-300/40 dark:border-violet-400/15 text-violet-900 dark:text-violet-100 placeholder:text-violet-700/50 dark:placeholder:text-violet-300/40 focus:outline-none focus:ring-2 focus:ring-violet-500/40 resize-none"
              data-testid="learn-feedback-comment-input"
              autoFocus
            />
            <div className="flex items-center justify-between mt-1">
              <span className={`text-[10px] ${charsLeft < 30 ? 'text-amber-600 dark:text-amber-400' : 'text-violet-700/55 dark:text-violet-300/45'}`}>
                {charsLeft} characters left
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => { setShowComment(false); setComment(''); }}
                  disabled={submitting}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold text-violet-700/85 dark:text-violet-300/70 hover:text-violet-900 dark:hover:text-violet-200"
                  data-testid="learn-feedback-comment-cancel"
                >
                  <X className="w-3 h-3" />
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={sendComment}
                  disabled={submitting || !comment.trim()}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
                  data-testid="learn-feedback-comment-send"
                >
                  <Send className="w-3 h-3" />
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
